export type AnswerValue = string;
export type ImpactKind = "normal" | "critical" | "special";
export type ImpactTarget = "player" | "bot";
export type BattlePhase = "playing" | "won" | "lost";
export type QuestionType = "single_choice" | "multiple_choice";
export const BATTLE_DURATION_SECONDS = 75;

export interface BattleOption {
  id: string;
  label: string;
}

export interface BattleQuestion {
  id: string;
  slug: string;
  prompt: readonly string[];
  spokenPrompt: string;
  questionType: QuestionType;
  difficulty: number;
  timeLimitSeconds: number;
  options: readonly BattleOption[];
  correctOptionIds?: readonly AnswerValue[];
}

export interface BattleConfiguration {
  playerHp: number;
  playerMaxHp: number;
  opponentHp: number;
  opponentMaxHp: number;
  difficulty: number;
}

export interface ServerAnswerResolution {
  isCorrect: boolean;
  isCritical: boolean;
  isSpecial: boolean;
  damageDealt: number;
  damageReceived: number;
  playerHp: number;
  opponentHp: number;
  status: "active" | "victory" | "defeat";
  combo: number;
  maxCombo: number;
  specialMeter: number;
  correctAnswers: number;
  totalAnswers: number;
}

export interface BattleImpact {
  amount: number;
  kind: ImpactKind;
  target: ImpactTarget;
  eventId: number;
}

export interface BattleMachineState {
  questions: readonly BattleQuestion[];
  playerHp: number;
  playerMaxHp: number;
  botHp: number;
  botMaxHp: number;
  secondsLeft: number;
  questionIndex: number;
  question: BattleQuestion;
  selectedAnswers: readonly AnswerValue[];
  feedback: "idle" | "correct" | "incorrect";
  locked: boolean;
  phase: BattlePhase;
  impact: BattleImpact;
  combo: number;
  maxCombo: number;
  specialMeter: number;
  correctAnswers: number;
  totalAnswers: number;
  difficulty: number;
}

export type BattleEvent =
  | { type: "ANSWER"; values: readonly AnswerValue[]; criticalRoll: number; useSpecial: boolean }
  | {
    type: "ANSWER_RESOLVED";
    values: readonly AnswerValue[];
    resolution: ServerAnswerResolution;
  }
  | {
    type: "CONFIGURE";
    questions: readonly BattleQuestion[];
    configuration?: BattleConfiguration;
  }
  | { type: "TICK" }
  | { type: "FEEDBACK_EXPIRED" }
  | { type: "RESET" };

export const BATTLE_QUESTIONS: readonly BattleQuestion[] = [
  demoQuestion("multiply-8-7", ["8", "×", "7", "=", "?"], "multiply-8-7:56", [
    ["multiply-8-7:48", "48"],
    ["multiply-8-7:54", "54"],
    ["multiply-8-7:56", "56"],
    ["multiply-8-7:64", "64"]
  ]),
  demoQuestion("add-9-6", ["9", "+", "6", "=", "?"], "add-9-6:15", [
    ["add-9-6:13", "13"],
    ["add-9-6:14", "14"],
    ["add-9-6:15", "15"],
    ["add-9-6:16", "16"]
  ]),
  demoQuestion("subtract-12-5", ["12", "−", "5", "=", "?"], "subtract-12-5:7", [
    ["subtract-12-5:5", "5"],
    ["subtract-12-5:6", "6"],
    ["subtract-12-5:7", "7"],
    ["subtract-12-5:8", "8"]
  ]),
  demoQuestion("multiply-6-6", ["6", "×", "6", "=", "?"], "multiply-6-6:36", [
    ["multiply-6-6:30", "30"],
    ["multiply-6-6:32", "32"],
    ["multiply-6-6:36", "36"],
    ["multiply-6-6:42", "42"]
  ])
] as const;

const DEFAULT_CONFIGURATION: BattleConfiguration = {
  playerHp: 82,
  playerMaxHp: 100,
  opponentHp: 65,
  opponentMaxHp: 100,
  difficulty: 1
};

export function createInitialBattleState(
  questions: readonly BattleQuestion[] = BATTLE_QUESTIONS,
  configuration: BattleConfiguration = DEFAULT_CONFIGURATION
): BattleMachineState {
  const playableQuestions = questions.length > 0 ? questions : BATTLE_QUESTIONS;
  return {
    questions: playableQuestions,
    playerHp: configuration.playerHp,
    playerMaxHp: configuration.playerMaxHp,
    botHp: configuration.opponentHp,
    botMaxHp: configuration.opponentMaxHp,
    secondsLeft: BATTLE_DURATION_SECONDS,
    questionIndex: 0,
    question: playableQuestions[0],
    selectedAnswers: [],
    feedback: "idle",
    locked: false,
    phase: "playing",
    impact: {
      amount: 0,
      kind: "normal",
      target: "bot",
      eventId: 0
    },
    combo: 0,
    maxCombo: 0,
    specialMeter: 0,
    correctAnswers: 0,
    totalAnswers: 0,
    difficulty: configuration.difficulty
  };
}

export function transitionBattle(
  state: BattleMachineState,
  event: BattleEvent
): BattleMachineState {
  switch (event.type) {
    case "RESET":
      return createInitialBattleState(state.questions);
    case "CONFIGURE":
      return createInitialBattleState(
        event.questions,
        event.configuration ?? DEFAULT_CONFIGURATION
      );
    case "TICK":
      return tickBattle(state);
    case "FEEDBACK_EXPIRED":
      return expireFeedback(state);
    case "ANSWER":
      return answerQuestion(state, event.values, event.criticalRoll, event.useSpecial);
    case "ANSWER_RESOLVED":
      return applyServerResolution(state, event.values, event.resolution);
  }
}

function tickBattle(state: BattleMachineState): BattleMachineState {
  if (state.phase !== "playing" || state.secondsLeft === 0) {
    return state;
  }

  const secondsLeft = state.secondsLeft - 1;
  return {
    ...state,
    secondsLeft,
    phase: secondsLeft === 0 ? "lost" : "playing"
  };
}

function expireFeedback(state: BattleMachineState): BattleMachineState {
  const shouldAdvance = state.feedback !== "idle" && state.phase === "playing";
  const hasNextQuestion = state.questionIndex + 1 < state.questions.length;

  if (shouldAdvance && !hasNextQuestion) {
    return {
      ...state,
      selectedAnswers: [],
      feedback: "idle",
      locked: true,
      phase: "lost"
    };
  }

  const questionIndex = shouldAdvance ? state.questionIndex + 1 : state.questionIndex;
  const question = state.questions[questionIndex];

  return {
    ...state,
    selectedAnswers: [],
    feedback: "idle",
    locked: false,
    questionIndex,
    question
  };
}

function answerQuestion(
  state: BattleMachineState,
  values: readonly AnswerValue[],
  criticalRoll: number,
  useSpecial: boolean
): BattleMachineState {
  if (state.locked || state.phase !== "playing") {
    return state;
  }

  const correct = exactSetMatch(values, state.question.correctOptionIds ?? []);
  if (correct) {
    const combo = state.combo + 1;
    const isSpecial = useSpecial && state.specialMeter === 100;
    const isCritical = !isSpecial && criticalRoll < 0.25;
    const damage = 16 + Math.min(combo - 1, 5) * 2
      + (isCritical ? 4 : 0)
      + (isSpecial ? 20 : 0);
    const botHp = Math.max(0, state.botHp - damage);
    const specialMeter = isSpecial ? 0 : Math.min(100, state.specialMeter + 25);

    return {
      ...state,
      selectedAnswers: values,
      feedback: "correct",
      locked: true,
      botHp,
      combo,
      maxCombo: Math.max(state.maxCombo, combo),
      specialMeter,
      correctAnswers: state.correctAnswers + 1,
      totalAnswers: state.totalAnswers + 1,
      phase: botHp === 0 ? "won" : "playing",
      impact: {
        amount: damage,
        kind: isSpecial ? "special" : isCritical ? "critical" : "normal",
        target: "bot",
        eventId: state.impact.eventId + 1
      }
    };
  }

  const damage = 8 + state.difficulty * 4;
  const playerHp = Math.max(0, state.playerHp - damage);
  return {
    ...state,
    selectedAnswers: values,
    feedback: "incorrect",
    locked: true,
    playerHp,
    combo: 0,
    specialMeter: Math.max(0, state.specialMeter - 20),
    totalAnswers: state.totalAnswers + 1,
    phase: playerHp === 0 ? "lost" : "playing",
    impact: {
      amount: damage,
      kind: "normal",
      target: "player",
      eventId: state.impact.eventId + 1
    }
  };
}

function applyServerResolution(
  state: BattleMachineState,
  values: readonly AnswerValue[],
  resolution: ServerAnswerResolution
): BattleMachineState {
  if (state.locked || state.phase !== "playing") {
    return state;
  }

  const damage = resolution.isCorrect
    ? resolution.damageDealt
    : resolution.damageReceived;

  return {
    ...state,
    selectedAnswers: values,
    feedback: resolution.isCorrect ? "correct" : "incorrect",
    locked: true,
    playerHp: resolution.playerHp,
    botHp: resolution.opponentHp,
    combo: resolution.combo,
    maxCombo: resolution.maxCombo,
    specialMeter: resolution.specialMeter,
    correctAnswers: resolution.correctAnswers,
    totalAnswers: resolution.totalAnswers,
    phase: resolution.status === "victory"
      ? "won"
      : resolution.status === "defeat"
        ? "lost"
        : "playing",
    impact: {
      amount: damage,
      kind: resolution.isSpecial
        ? "special"
        : resolution.isCritical
          ? "critical"
          : "normal",
      target: resolution.isCorrect ? "bot" : "player",
      eventId: state.impact.eventId + 1
    }
  };
}

export function exactSetMatch(
  selected: readonly string[],
  correct: readonly string[]
): boolean {
  if (selected.length !== correct.length) {
    return false;
  }
  const selectedSet = new Set(selected);
  return selectedSet.size === correct.length
    && correct.every((value) => selectedSet.has(value));
}

function demoQuestion(
  id: string,
  prompt: readonly string[],
  correct: string,
  options: ReadonlyArray<readonly [string, string]>
): BattleQuestion {
  return {
    id,
    slug: id,
    prompt,
    spokenPrompt: prompt.join(" "),
    questionType: "single_choice",
    difficulty: 1,
    timeLimitSeconds: 20,
    options: options.map(([optionId, label]) => ({ id: optionId, label })),
    correctOptionIds: [correct]
  };
}
